import arrayLength from "@-/com.npmjs.extra-array__length";
import type { DecompositionMap } from "#decomposition-map";

/**
 * Every grapheme reachable from `glyph` through the decomposition map: its direct components,
 * their components, and so on down to the strokes. The glyph itself is never included, even when
 * the map cycles back to it (the static data has a few such loops, e.g. 龍 ↔ 𳑳), and every other
 * node is visited once, so the walk terminates on any map.
 *
 * Breadth-first with a head-index queue rather than `Array.prototype.shift()` (O(n) per shift on
 * V8/Hermes), the same shape `component-prevalence-lookup` uses.
 */
export function collectTransitiveComponents(
  glyph: string,
  decompositionMap: DecompositionMap,
): ReadonlySet<string> {
  const components = new Set<string>();
  const queue: string[] = [glyph];
  for (let head = 0; head < arrayLength(queue); head++) {
    const current = queue[head];
    if (current === undefined) {
      continue;
    }
    const directComponents = decompositionMap.get(current);
    if (directComponents === undefined) {
      continue;
    }
    for (const component of directComponents) {
      if (component === glyph || components.has(component)) {
        continue;
      }
      components.add(component);
      queue.push(component);
    }
  }
  return components;
}
